import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { dashboardAPI, referenceAPI, surveyAPI } from '../services/api'
import SurveyReportsModal from '../components/SurveyReportsModal'
import StatCard from '../components/StatCard'
import PageHeader from '../components/PageHeader'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line, Doughnut, Pie } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

const Dashboard = () => {
  const { isStaff, isClientuser, isSuperuser, isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    skip_date_filter: true, // Default to showing all data
    client: '',
    survey_type: '',
    country: '',
    port: '',
    surveyor: '',
    payment_status: '',
  })
  const [activeFilterField, setActiveFilterField] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({
    client: [],
    survey_type: [],
    country: [],
    port: [],
    surveyor: [],
    payment_status: [],
  })
  const [tempSelections, setTempSelections] = useState({
    client: [],
    survey_type: [],
    country: [],
    port: [],
    surveyor: [],
    payment_status: [],
  })
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    surveyTypes: [],
    countries: [],
    ports: [],
    surveyors: [],
  })
  const [showFilterFrame, setShowFilterFrame] = useState(true)
  const [selectedQuickDateFilter, setSelectedQuickDateFilter] = useState('')
  const [surveys, setSurveys] = useState([])
  const [surveysLoading, setSurveysLoading] = useState(false)
  const [surveyPage, setSurveyPage] = useState(1)
  const [surveyTotalPages, setSurveyTotalPages] = useState(1)
  const [surveyTotalCount, setSurveyTotalCount] = useState(0)
  const [reportsModalRef, setReportsModalRef] = useState(null)

  // SoA state
  const [soaData, setSoaData] = useState(null)
  const [showSoaModal, setShowSoaModal] = useState(false)
  const [soaLoading, setSoaLoading] = useState(false)

  useEffect(() => {
    if (!isStaff && !isClientuser) {
      window.location.href = '/operations'
      return
    }
    loadFilterOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, isClientuser])

  // Fetch stats and surveys when filters change (including initial load)
  useEffect(() => {
    if (isStaff || isClientuser) {
      fetchStats()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isStaff, isClientuser])

  const loadFilterOptions = async () => {
    try {
      const promises = [
        referenceAPI.getClients(),
        referenceAPI.getSurveyTypes(),
        referenceAPI.getCountries(),
      ]
      
      // Only load surveyors if not a clientuser
      if (!isClientuser) {
        promises.push(referenceAPI.getSurveyorsByType())
      }
      
      const results = await Promise.all(promises)
      const [clientsRes, typesRes, countriesRes, surveyorsRes] = results
      
      setFilterOptions(prev => ({
        ...prev,
        clients: clientsRes.data.clients || [],
        surveyTypes: typesRes.data.survey_types || [],
        countries: countriesRes.data.countries || [],
        surveyors: isClientuser ? [] : (surveyorsRes?.data?.surveyors || []).map(s => s.code || s.name), // Extract codes
      }))
    } catch (error) {
      console.error('Error loading filter options:', error)
    }
  }

  const loadPorts = async (country) => {
    if (!country) {
      setFilterOptions(prev => ({ ...prev, ports: [] }))
      return
    }
    try {
      const response = await referenceAPI.getPorts(country)
      setFilterOptions(prev => ({
        ...prev,
        ports: response.data.ports || [],
      }))
    } catch (error) {
      console.error('Error loading ports:', error)
    }
  }

  useEffect(() => {
    if (filters.country) {
      // If multiple countries selected, use the first one for port loading
      const countryForPorts = filters.country.split(',')[0].trim()
      loadPorts(countryForPorts)
    } else {
      setFilterOptions(prev => ({ ...prev, ports: [] }))
      setSelectedFilters(prev => ({ ...prev, port: [] }))
      setFilters(prev => ({ ...prev, port: '' }))
    }
  }, [filters.country])

  // Close dropdown when clicking outside (optional - can keep field selected)
  // Users can manually deselect by choosing empty option

  const fetchSurveys = async (page = surveyPage) => {
    setSurveysLoading(true)
    try {
      const params = {
        page: page,
      }
      
      // Handle date filters - same as Dashboard stats
      if (filters.skip_date_filter) {
        params.skip_date_filter = 'true'
      } else {
        if (filters.start_date) params.start_date = filters.start_date
        if (filters.end_date) params.end_date = filters.end_date
      }
      
      // Add other filters only if they have values - same as Dashboard stats
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      const response = await surveyAPI.list(params)
      if (response.data.results) {
        setSurveys(response.data.results)
        setSurveyTotalPages(response.data.total_pages || 1)
        setSurveyTotalCount(response.data.count || 0)
      }
    } catch (error) {
      console.error('Error fetching surveys:', error)
    } finally {
      setSurveysLoading(false)
    }
  }

  const fetchStats = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      
      // Handle date filters
      if (filters.skip_date_filter) {
        // Skip date filter - show all dates
        params.append('skip_date_filter', 'true')
      } else {
        // Apply date filters if skip_date_filter is false
        if (filters.start_date) params.append('start_date', filters.start_date)
        if (filters.end_date) params.append('end_date', filters.end_date)
      }
      
      // Add other filters only if they have values
      if (filters.client) params.append('client', filters.client)
      if (filters.survey_type) params.append('survey_type', filters.survey_type)
      if (filters.country) params.append('country', filters.country)
      if (filters.port) params.append('port', filters.port)
      if (filters.surveyor) params.append('surveyor', filters.surveyor)
      if (filters.payment_status) params.append('payment_status', filters.payment_status)

      const response = await dashboardAPI.getStats(params)
      if (response.data.status === 'success') {
        setStats(response.data.data)
      }
      
      // Fetch surveys with the same filters (reset to page 1)
      setSurveyPage(1)
      await fetchSurveys(1)
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value }
      // Clear port if country changes
      if (key === 'country' && value !== prev.country) {
        newFilters.port = ''
        setSelectedFilters(prev => ({ ...prev, port: [] }))
      }
      // When dates are manually set, disable skip_date_filter
      if (key === 'start_date' || key === 'end_date') {
        newFilters.skip_date_filter = false
      }
      return newFilters
    })
  }

  const toggleTempSelection = (field, option) => {
    setTempSelections(prev => {
      const current = prev[field] || []
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option]
      return { ...prev, [field]: updated }
    })
  }

  const handleAddToFilter = () => {
    if (!activeFilterField) return
    
    // Add temporary selections to actual filters
    setSelectedFilters(prev => {
      const newFilters = { ...prev }
      if (activeFilterField !== 'date') {
        newFilters[activeFilterField] = [...tempSelections[activeFilterField]]
        setFilters(prevFilters => ({
          ...prevFilters,
          [activeFilterField]: tempSelections[activeFilterField].length > 0 
            ? tempSelections[activeFilterField].join(',') 
            : ''
        }))
      }
      return newFilters
    })
    
    // Close the options window
    setActiveFilterField('')
  }

  const toggleFilterOption = (field, option) => {
    setSelectedFilters(prev => {
      const current = prev[field] || []
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option]
      
      // Update filters object with comma-separated values
      setFilters(prevFilters => ({
        ...prevFilters,
        [field]: updated.length > 0 ? updated.join(',') : ''
      }))
      
      return { ...prev, [field]: updated }
    })
  }

  const handleApplyFilters = (e) => {
    e.preventDefault()
    setActiveFilterField('')
    setSurveyPage(1) // Reset to first page when filters are applied
    fetchStats()
  }

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      skip_date_filter: true, // Reset to show all data
      client: '',
      survey_type: '',
      country: '',
      port: '',
      surveyor: '',
      payment_status: '',
    })
    setSelectedFilters({
      client: [],
      survey_type: [],
      country: [],
      port: [],
      surveyor: [],
      payment_status: [],
    })
    setTempSelections({
      client: [],
      survey_type: [],
      country: [],
      port: [],
      surveyor: [],
      payment_status: [],
    })
    setActiveFilterField('')
    setSelectedQuickDateFilter('') // Reset Quick Date Filters dropdown
    // Fetch stats after clearing to show all data
    setTimeout(() => fetchStats(), 100)
  }

  const handleExportExcel = async () => {
    try {
      // Build params object from current filters (same as fetchStats)
      const params = {}
      
      // Handle date filters
      if (filters.skip_date_filter) {
        params.skip_date_filter = 'true'
      } else {
        if (filters.start_date) params.start_date = filters.start_date
        if (filters.end_date) params.end_date = filters.end_date
      }
      
      // Add other filters only if they have values
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      // Call export API
      const response = await surveyAPI.export(params)
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Try to extract filename from Content-Disposition header, otherwise generate it
      let filename = 'KBL_Surveys.xlsx'
      // Axios normalizes headers to lowercase
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      } else {
        // Fallback: generate filename based on user role and date
        const today = new Date().toISOString().split('T')[0].replace(/-/g, '')
        filename = isSuperuser
          ? `KBL_Surveys_admin_${today}.xlsx`
          : isClientuser
            ? `KBL_Surveys_client_${today}.xlsx`
            : `KBL_Surveys_staff_${today}.xlsx`
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      alert('Failed to export surveys to Excel. Please try again.')
    }
  }

  // SoA Functions
  const generateSOA = async () => {
    if (!isAdmin) {
      alert('Only Admin users can generate Statement of Account')
      return
    }
    
    setSoaLoading(true)
    try {
      // Build params object from current filters (same as fetchStats)
      const params = {}
      
      // Add date filters
      if (!filters.skip_date_filter && filters.start_date && filters.end_date) {
        params.start_date = filters.start_date
        params.end_date = filters.end_date
      } else {
        params.skip_date_filter = 'true'
      }
      
      // Add other filters
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      const response = await dashboardAPI.generateSOA(params)
      setSoaData(response.data)
      setShowSoaModal(true)
    } catch (error) {
      console.error('Error generating SoA:', error)
      alert('Failed to generate Statement of Account. Please try again.')
    } finally {
      setSoaLoading(false)
    }
  }

  const exportSOA = async () => {
    if (!isAdmin) {
      alert('Only Admin users can export Statement of Account')
      return
    }
    
    try {
      // Build params object from current filters (same as fetchStats)
      const params = {}
      
      // Add date filters
      if (!filters.skip_date_filter && filters.start_date && filters.end_date) {
        params.start_date = filters.start_date
        params.end_date = filters.end_date
      } else {
        params.skip_date_filter = 'true'
      }
      
      // Add other filters
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      console.log('Exporting SoA with params:', params)
      
      const response = await dashboardAPI.exportSOA(params)
      
      console.log('SoA export response:', response)
      
      // Check if response is an error (JSON instead of blob)
      if (response.data && typeof response.data === 'object' && response.data.error) {
        throw new Error(response.data.error)
      }
      
      // Create blob URL and trigger download - same pattern as survey export
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Try to extract filename from Content-Disposition header, otherwise generate it
      let filename = 'KBL_SoA.xlsx'
      // Axios normalizes headers to lowercase
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      } else {
        // Fallback: generate filename with timestamp
        const today = new Date().toISOString().split('T')[0].replace(/-/g, '')
        const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '')
        filename = `KBL_SoA_${today}_${time}.xlsx`
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting SoA:', error)
      
      // Provide more specific error messages
      let errorMessage = 'Failed to export Statement of Account. Please try again.'
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 403) {
          errorMessage = 'Access denied. Only Admin users can export Statement of Account.'
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error
        } else if (error.response.status === 500) {
          errorMessage = 'Server error occurred while exporting. Please try again.'
        }
      } else if (error.message) {
        errorMessage = error.message
      }
      
      alert(errorMessage)
    }
  }

  const handleQuickDateFilter = (preset) => {
    setSelectedQuickDateFilter(preset)
    const today = new Date()
    let startDate = ''
    let endDate = ''
    
    switch(preset) {
      case 'all':
        setFilters(prev => ({ ...prev, skip_date_filter: true, start_date: '', end_date: '' }))
        return
      case 'this-month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
        endDate = today.toISOString().split('T')[0]
        break
      case 'this-quarter':
        const quarter = Math.floor(today.getMonth() / 3)
        startDate = new Date(today.getFullYear(), quarter * 3, 1).toISOString().split('T')[0]
        endDate = today.toISOString().split('T')[0]
        break
      case 'this-year':
        startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0]
        endDate = today.toISOString().split('T')[0]
        break
      case 'last-7-days':
        const last7 = new Date(today)
        last7.setDate(today.getDate() - 7)
        startDate = last7.toISOString().split('T')[0]
        endDate = today.toISOString().split('T')[0]
        break
      case 'last-30-days':
        const last30 = new Date(today)
        last30.setDate(today.getDate() - 30)
        startDate = last30.toISOString().split('T')[0]
        endDate = today.toISOString().split('T')[0]
        break
      case 'last-3-months':
        const last3Months = new Date(today)
        last3Months.setMonth(today.getMonth() - 3)
        startDate = last3Months.toISOString().split('T')[0]
        endDate = today.toISOString().split('T')[0]
        break
      case 'last-year':
        startDate = new Date(today.getFullYear() - 1, 0, 1).toISOString().split('T')[0]
        endDate = new Date(today.getFullYear() - 1, 11, 31).toISOString().split('T')[0]
        break
      default:
        return
    }
    
    setFilters(prev => ({ 
      ...prev, 
      start_date: startDate, 
      end_date: endDate,
      skip_date_filter: false 
    }))
  }

  const handleFieldChange = (field) => {
    const newField = field === activeFilterField ? '' : field
    setActiveFilterField(newField)
    
    // Enable date inputs when Date filter is selected
    if (newField === 'date') {
      setFilters(prev => ({ ...prev, skip_date_filter: false }))
    }
    
    // Initialize temporary selections with current filter values when opening a field
    if (newField && newField !== 'date') {
      const currentValues = selectedFilters[newField] || []
      setTempSelections(prev => ({ ...prev, [newField]: [...currentValues] }))
    }
    
    // Load ports if country is selected and port field is chosen
    if (newField === 'port' && filters.country) {
      const countryForPorts = filters.country.split(',')[0].trim()
      loadPorts(countryForPorts)
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kbl-primary"></div>
      </div>
    )
  }

  const trendData = stats?.surveys_by_month || []
  const typeData = stats?.surveys_by_type || []

  // Filter trend data for visualization only - show up to last day of previous month
  const getFilteredTrendDataForViz = (data) => {
    if (!data || data.length === 0) return []

    const now = new Date()
    // Get last day of previous month
    const lastDayOfPrevMonth = new Date(now.getFullYear(), now.getMonth(), 0)

    return data.filter(item => {
      if (!item.month) return false
      // Parse month string like "Jan 2024" or "January 2024"
      const monthDate = new Date(item.month + ' 1')
      if (isNaN(monthDate.getTime())) return false
      // Include only if month is before or equal to last day of previous month
      return monthDate <= lastDayOfPrevMonth
    })
  }

  // Use filtered data for charts (visualization only), keep original for stats
  const vizTrendData = getFilteredTrendDataForViz(trendData)

  return (
    <>
      <div className="page-container">
      <PageHeader title="KBL Dashboard" description="Survey statistics and analytics">
        <button
          onClick={() => setShowFilterFrame(!showFilterFrame)}
          className={`p-2 rounded-lg border transition-colors ${showFilterFrame ? 'border-[#ea580c] bg-[#fff7ed] text-[#c2410c]' : 'border-[#efe3d6] bg-white text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed]'}`}
          title="Toggle Filters"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </PageHeader>

      {/* Professional Filter Frame - 4 Column Layout */}
      {showFilterFrame && (
        <div className="bg-white rounded-xl border border-[#efe3d6] shadow-[0_1px_6px_rgba(234,88,12,0.12)] overflow-hidden mb-6">
          <div className="table-head-premium px-5 py-2">
            <h3 className="text-sm font-semibold flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-white" aria-hidden="true">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                </span>
                <span>Filter Options</span>
              </div>
              <button
                onClick={() => setShowFilterFrame(false)}
                className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-all duration-200 hover:scale-110 active:scale-95"
                title="Close Filters"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </h3>
          </div>
        
        <form onSubmit={handleApplyFilters} className="p-5">
          {/* 3 Column Layout (Filter Field, Options, Quick Date Filters) - 40% 40% 20% */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            {/* Column 1: Filter Fields - 40% width */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter Field</label>
              <select
                value={activeFilterField}
                onChange={(e) => handleFieldChange(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white text-gray-800 transition-colors mb-4"
              >
                <option value="">🔍 Select Filter Field</option>
                <option value="date">📅 Date</option>
                <option value="client">👤 Client</option>
                <option value="survey_type">📋 Survey Type</option>
                <option value="country">🌍 Country</option>
                <option value="port">⚓ Port</option>
                {!isClientuser && <option value="surveyor">👨‍💼 Surveyor</option>}
                <option value="payment_status">💰 Payment Status</option>
              </select>
            </div>

            {/* Column 2: Filter Options - Always rendered to maintain grid structure - 40% width */}
            <div className="md:col-span-2">
              {activeFilterField && (
                <div className="border border-[#efe3d6] rounded-lg bg-white shadow-sm">
                  {/* Options Header */}
                  <div className="flex items-center justify-between p-3 border-b border-[#efe3d6] bg-[#fff7ed]">
                    <span className="text-sm font-semibold text-[#9a3412]">
                      {activeFilterField === 'date' ? 'Select Date Range' : 
                       `Select ${activeFilterField === 'client' ? 'Clients' : activeFilterField === 'survey_type' ? 'Survey Types' : activeFilterField === 'country' ? 'Countries' : activeFilterField === 'port' ? 'Ports' : 'Surveyors'} (Multiple)`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveFilterField('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Close"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Options Content */}
                  <div className="max-h-[200px] overflow-y-auto p-3">
                  {activeFilterField === 'date' && (
                    <div className="space-y-3">
                      {/* Two Column Grid for Dates */}
                      <div className="grid grid-cols-2 gap-4">
                        {/* Left Column - Start Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Start Date
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              id="start-date-input"
                              value={filters.start_date}
                              onChange={(e) => {
                                handleFilterChange('start_date', e.target.value)
                              }}
                              disabled={filters.skip_date_filter}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white text-gray-800 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <div 
                              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                              onClick={(e) => {
                                e.stopPropagation()
                                const input = document.getElementById('start-date-input')
                                if (input && !input.disabled) {
                                  if (typeof input.showPicker === 'function') {
                                    input.showPicker()
                                  } else {
                                    input?.click()
                                  }
                                }
                              }}
                            >
                              <svg 
                                className="w-5 h-5 text-orange-500 transition-transform hover:scale-110" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                        
                        {/* Right Column - End Date */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                            <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            End Date
                          </label>
                          <div className="relative">
                            <input
                              type="date"
                              id="end-date-input"
                              value={filters.end_date}
                              onChange={(e) => {
                                handleFilterChange('end_date', e.target.value)
                              }}
                              disabled={filters.skip_date_filter}
                              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white text-gray-800 transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <div 
                              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                              onClick={(e) => {
                                e.stopPropagation()
                                const input = document.getElementById('end-date-input')
                                if (input && !input.disabled) {
                                  if (typeof input.showPicker === 'function') {
                                    input.showPicker()
                                  } else {
                                    input?.click()
                                  }
                                }
                              }}
                            >
                              <svg 
                                className="w-5 h-5 text-orange-500 transition-transform hover:scale-110" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                      {/* Apply Button */}
                      <div className="pt-3 border-t border-gray-200 flex justify-center">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveFilterField('')
                            fetchStats()
                          }}
                          disabled={!filters.start_date || !filters.end_date}
                          className="w-auto px-4 py-1.5 text-sm bg-white border border-[#efe3d6] text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed] font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Apply Date Range
                        </button>
                      </div>
                    </div>
                  )}
                  {activeFilterField === 'client' && (
                    <div className="space-y-1">
                      {filterOptions.clients.length > 0 ? (
                        filterOptions.clients.map(client => (
                          <label key={client} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.client.includes(client)}
                              onChange={() => toggleTempSelection('client', client)}
                              className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{client}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No clients available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'survey_type' && (
                    <div className="space-y-1">
                      {filterOptions.surveyTypes.length > 0 ? (
                        filterOptions.surveyTypes.map(type => (
                          <label key={type} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.survey_type.includes(type)}
                              onChange={() => toggleTempSelection('survey_type', type)}
                              className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{type}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No survey types available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'country' && (
                    <div className="space-y-1">
                      {filterOptions.countries.length > 0 ? (
                        filterOptions.countries.map(country => (
                          <label key={country} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.country.includes(country)}
                              onChange={() => toggleTempSelection('country', country)}
                              className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{country}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No countries available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'port' && (
                    <div className="space-y-1">
                      {filters.country ? (
                        filterOptions.ports.length > 0 ? (
                          <>
                            {filters.country.includes(',') && (
                              <p className="text-xs text-orange-600 mb-2 px-2">
                                Note: Ports shown for first selected country
                              </p>
                            )}
                            {filterOptions.ports.map(port => (
                              <label key={port} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={tempSelections.port.includes(port)}
                                  onChange={() => toggleTempSelection('port', port)}
                                  className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                                />
                                <span className="text-sm text-gray-700">{port}</span>
                              </label>
                            ))}
                          </>
                        ) : (
                          <p className="text-sm text-gray-500 py-2">No ports available for selected country</p>
                        )
                      ) : (
                        <p className="text-sm text-gray-500 py-2">Please select a country first</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'surveyor' && !isClientuser && (
                    <div className="space-y-1">
                      {filterOptions.surveyors.length > 0 ? (
                        filterOptions.surveyors.map(surveyor => (
                          <label key={surveyor} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.surveyor.includes(surveyor)}
                              onChange={() => toggleTempSelection('surveyor', surveyor)}
                              className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                            />
                            <span className="text-sm text-gray-700">{surveyor}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No surveyors available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'payment_status' && (
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={tempSelections.payment_status.includes('Unpaid')}
                          onChange={() => toggleTempSelection('payment_status', 'Unpaid')}
                          className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700">💰 Unpaid (No Received Payment Date)</span>
                      </label>
                    </div>
                  )}
                  </div>
                  {/* Add to Filter Button - Only show for fields that need it */}
                  {activeFilterField && activeFilterField !== 'date' && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddToFilter}
                        className="w-auto px-4 py-1.5 text-sm bg-white border border-[#efe3d6] text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed] font-semibold rounded-lg transition-colors flex items-center justify-center gap-2 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-200 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add to Filter
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Column 3: Quick Date Filters - Dropdown - Always in last position - 20% width */}
            <div className="md:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quick Date Filters</label>
              <select
                value={selectedQuickDateFilter}
                onChange={(e) => {
                  if (e.target.value) {
                    handleQuickDateFilter(e.target.value)
                  }
                }}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white text-gray-800 transition-colors"
              >
                <option value="">📅 Select Quick Date Filter</option>
                <option value="all">📅 All</option>
                <option value="this-month">🗓️ This Month</option>
                <option value="this-quarter">📈 This Quarter</option>
                <option value="this-year">📉 This Year</option>
                <option value="last-7-days">⏱️ Last 7 Days</option>
                <option value="last-30-days">⏰ Last 30 Days</option>
                <option value="last-3-months">📅 Last 3 Months</option>
                <option value="last-year">📆 Last Year</option>
              </select>
            </div>

          </div>

          {/* Footer: Selected Filters (Left) and Action Buttons (Right) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
            {/* Selected Filters - Left side, single line */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Selected Filters:</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedFilters.client.map(client => (
                    <span key={client} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      👤 {client}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('client', client)}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.survey_type.map(type => (
                    <span key={type} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      📋 {type}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('survey_type', type)}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.country.map(country => (
                    <span key={country} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      🌍 {country}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('country', country)}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.port.map(port => (
                    <span key={port} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      ⚓ {port}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('port', port)}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.surveyor.map(surveyor => (
                    <span key={surveyor} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      👨‍💼 {surveyor}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('surveyor', surveyor)}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.payment_status.map(status => (
                    <span key={status} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      💰 {status}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('payment_status', status)}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {(filters.start_date || filters.end_date) && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                      📅 {filters.start_date || '...'} to {filters.end_date || '...'}
                      <button
                        type="button"
                        onClick={() => {
                          handleFilterChange('start_date', '')
                          handleFilterChange('end_date', '')
                        }}
                        className="hover:text-[#c2410c]"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {selectedFilters.client.length === 0 && selectedFilters.survey_type.length === 0 &&
                   selectedFilters.country.length === 0 && selectedFilters.port.length === 0 &&
                   selectedFilters.surveyor.length === 0 && selectedFilters.payment_status.length === 0 &&
                   !filters.start_date && !filters.end_date && (
                    <span className="text-xs text-gray-400 italic">No filters selected</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons - Right end */}
            <div className="flex gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear All
              </button>
              
              {/* SoA Button - Admin Only */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={generateSOA}
                  disabled={soaLoading}
                  className="px-4 py-2 text-sm bg-white border border-[#efe3d6] text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed] font-semibold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {soaLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      SoA
                    </>
                  )}
                </button>
              )}
              
              <button
                type="button"
                onClick={handleExportExcel}
                className="px-4 py-2 text-sm bg-white border border-[#efe3d6] text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed] font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export Excel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-premium text-white font-semibold rounded-lg shadow-[0_2px_8px_rgba(234,88,12,0.3)] hover:shadow-[0_4px_14px_rgba(234,88,12,0.4)] transition-shadow flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Apply Filters
              </button>
            </div>
          </div>
        </form>
        </div>
      )}

      {/* Stats Cards */}
      {(() => {
        const uniqueClients = stats?.surveys_by_client ? new Set(stats.surveys_by_client.map(c => c.client_name)).size : 0
        const countries = stats?.surveys_by_country ? new Set(stats.surveys_by_country.map(c => c.survey_country)).size : 0
        const showFinancials = isClientuser || isSuperuser
        const statCards = [
          { icon: 'bi-building', label: isClientuser ? 'Your Team' : 'Unique Clients', value: uniqueClients },
          !isClientuser && { icon: 'bi-people', label: 'Active Surveyors', value: stats?.surveys_by_surveyor?.length || 0 },
          { icon: 'bi-clipboard-data', label: 'Total Surveys', value: (stats?.total_surveys || 0).toLocaleString('en-US') },
          { icon: 'bi-globe', label: isClientuser ? 'Your Total Countries' : 'Countries Covered', value: countries },
          showFinancials && {
            icon: 'bi-fuel-pump',
            label: 'Total Recovered Fuel (MT)',
            value: stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000',
          },
          showFinancials && {
            icon: 'bi-cash-stack',
            label: 'Total Saving',
            value: `$${stats?.total_saving ? parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}`,
          },
        ].filter(Boolean)
        return (
          <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8 ${statCards.length >= 6 ? 'xl:grid-cols-6' : statCards.length === 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
            {statCards.map((card) => (
              <StatCard key={card.label} icon={card.icon} label={card.label} value={card.value} />
            ))}
          </div>
        )
      })()}

      {/* Analytics Row */}
      <div className="mb-8">
        <h2 className="mb-4 flex items-center gap-2.5 text-xl font-semibold">
          <i className="bi bi-graph-up-arrow icon-premium"></i>
          <span className="text-premium">Analytics</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Survey Trends Card */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-graph-up icon-premium"></i>
                  <span className="truncate max-w-[25ch]">Total Surveys</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {trendData.reduce((sum, d) => sum + d.count, 0)}
                </div>
              </div>
            </h2>
            {vizTrendData.length > 0 ? (
              <div className="h-[200px] relative">
                <Line
                  data={{
                    labels: vizTrendData.map(d => d.month),
                    datasets: [{
                      label: 'Number of Surveys',
                      data: vizTrendData.map(d => d.count),
                      borderColor: 'rgb(234, 88, 12)',
                      backgroundColor: 'rgba(249, 115, 22, 0.12)',
                      tension: 0.4,
                      fill: true,
                      borderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                      },
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>

          {/* Recovered Fuel Card */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-fuel-pump icon-premium"></i>
                  <span className="truncate max-w-[25ch]">Recovered Fuel</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toFixed(3) : '0.000'}
                </div>
              </div>
            </h2>
            {vizTrendData.length > 0 ? (
              <div className="h-[200px] relative">
                <Line
                  data={{
                    labels: vizTrendData.map(d => d.month),
                    datasets: [{
                      label: 'Recovered Fuel (MT)',
                      data: vizTrendData.map(d => {
                        // Calculate fuel for this month's surveys (approximation)
                        const monthlyTotal = stats?.total_recovered_fuel_mt || 0;
                        const totalSurveys = trendData.reduce((sum, d) => sum + d.count, 0);
                        const avgFuelPerSurvey = totalSurveys > 0 ? monthlyTotal / totalSurveys : 0;
                        return parseFloat((avgFuelPerSurvey * d.count).toFixed(3));
                      }),
                      borderColor: 'rgb(234, 88, 12)',
                      backgroundColor: 'rgba(249, 115, 22, 0.12)',
                      tension: 0.4,
                      fill: true,
                      borderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                        ticks: {
                          callback: function(value) {
                            return value.toFixed(1) + ' MT'
                          }
                        }
                      },
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px]">
                <div className="text-2xl font-semibold tabular-nums mb-2 text-premium">
                  {stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toFixed(3) : '0.000'}
                </div>
                <div className="text-sm font-medium text-slate-500">Total Metric Tons</div>
              </div>
            )}
          </div>

          {/* Saving Card */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-cash-stack icon-premium"></i>
                  <span className="truncate max-w-[25ch]">Total Saving</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  ${stats?.total_saving ? parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'}
                </div>
              </div>
            </h2>
            {vizTrendData.length > 0 ? (
              <div className="h-[200px] relative">
                <Line
                  data={{
                    labels: vizTrendData.map(d => d.month),
                    datasets: [{
                      label: 'Savings ($)',
                      data: vizTrendData.map(d => {
                        // Calculate savings for this month's surveys (approximation)
                        const monthlyTotal = stats?.total_saving || 0;
                        const totalSurveys = trendData.reduce((sum, d) => sum + d.count, 0);
                        const avgSavingPerSurvey = totalSurveys > 0 ? monthlyTotal / totalSurveys : 0;
                        return parseFloat((avgSavingPerSurvey * d.count).toFixed(2));
                      }),
                      borderColor: 'rgb(234, 88, 12)',
                      backgroundColor: 'rgba(249, 115, 22, 0.12)',
                      tension: 0.4,
                      fill: true,
                      borderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                        ticks: {
                          callback: function(value) {
                            return '$' + (value / 1000).toFixed(0) + 'K'
                          }
                        }
                      },
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px]">
                <div className="text-2xl font-semibold tabular-nums mb-2 text-premium">
                  ${stats?.total_saving ? parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </div>
                <div className="text-sm font-medium text-slate-500">Total Savings</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Donut Charts Row */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Survey Types Donut Chart */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-pie-chart icon-premium"></i>
                  <span>Survey Types</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {typeData.length}
                </div>
              </div>
            </h2>
            {typeData.length > 0 ? (
              <div className="h-[200px] flex items-center">
                <div className="w-1/2 relative max-h-[180px] flex items-center justify-center p-1">
                  <div className="w-full h-full">
                    <Doughnut
                    data={{
                      labels: typeData.map(d => d.survey_type),
                      datasets: [{
                        data: typeData.map(d => d.count),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        hoverBorderWidth: 3,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '65%',
                      plugins: {
                        legend: { display: false },
                      },
                    }}
                  />
                  </div>
                </div>
                <div className="w-1/2 max-h-[180px] overflow-y-auto pl-4">
                  <div className="space-y-1 text-xs">
                    {typeData.map((item, index) => {
                      const total = typeData.reduce((sum, d) => sum + d.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex items-center gap-2 justify-end">
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium truncate max-w-[35ch]" style={{ color: '#374151' }}>
                              {item.survey_type}
                            </div>
                            <div className="text-gray-500 whitespace-nowrap">
                              ({percentage}%)
                            </div>
                          </div>
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 -ml-1" 
                            style={{ backgroundColor: [
                              'rgba(59, 130, 246, 0.85)',
                              'rgba(16, 185, 129, 0.85)',
                              'rgba(251, 146, 60, 0.85)',
                              'rgba(239, 68, 68, 0.85)',
                              'rgba(168, 85, 247, 0.85)',
                              'rgba(236, 72, 153, 0.85)',
                              'rgba(139, 92, 246, 0.85)',
                              'rgba(34, 197, 94, 0.85)',
                            ][index] }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>

          {/* Countries Donut Chart */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-globe icon-premium"></i>
                  <span>Countries</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {stats?.surveys_by_country ? stats.surveys_by_country.length : 0}
                </div>
              </div>
            </h2>
            {stats?.surveys_by_country && stats.surveys_by_country.length > 0 ? (
              <div className="h-[200px] flex items-center">
                <div className="w-1/2 relative max-h-[180px] flex items-center justify-center p-1">
                  <div className="w-full h-full">
                    <Pie
                    data={{
                      labels: stats.surveys_by_country.map(c => c.survey_country),
                      datasets: [{
                        data: stats.surveys_by_country.map(c => c.count),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        hoverBorderWidth: 3,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                      },
                    }}
                  />
                  </div>
                </div>
                <div className="w-1/2 max-h-[180px] overflow-y-auto pl-4">
                  <div className="space-y-1 text-xs">
                    {stats.surveys_by_country.map((item, index) => {
                      const total = stats.surveys_by_country.reduce((sum, c) => sum + c.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex items-center gap-2 justify-end">
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium truncate max-w-[35ch]" style={{ color: '#374151' }}>
                              {item.survey_country}
                            </div>
                            <div className="text-gray-500 whitespace-nowrap">
                              ({percentage}%)
                            </div>
                          </div>
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 -ml-1" 
                            style={{ backgroundColor: [
                              'rgba(59, 130, 246, 0.85)',
                              'rgba(16, 185, 129, 0.85)',
                              'rgba(251, 146, 60, 0.85)',
                              'rgba(239, 68, 68, 0.85)',
                              'rgba(168, 85, 247, 0.85)',
                              'rgba(236, 72, 153, 0.85)',
                              'rgba(139, 92, 246, 0.85)',
                              'rgba(34, 197, 94, 0.85)',
                            ][index] }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>

          {/* Clients Donut Chart */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-building icon-premium"></i>
                  <span>Clients</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {stats?.surveys_by_client ? stats.surveys_by_client.length : 0}
                </div>
              </div>
            </h2>
            {stats?.surveys_by_client && stats.surveys_by_client.length > 0 ? (
              <div className="h-[200px] flex items-center">
                <div className="w-1/2 relative max-h-[180px] flex items-center justify-center p-1">
                  <div className="w-full h-full">
                    <Doughnut
                    data={{
                      labels: stats.surveys_by_client.map(c => c.client_name),
                      datasets: [{
                        data: stats.surveys_by_client.map(c => c.count),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 3,
                        hoverBorderWidth: 4,
                        hoverOffset: 8,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '75%',
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          callbacks: {
                            label: function(context) {
                              const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                              const percentage = ((context.parsed / total) * 100).toFixed(1);
                              return `${context.label}: ${context.parsed} (${percentage}%)`;
                            }
                          }
                        }
                      },
                      animation: {
                        animateRotate: true,
                        animateScale: false,
                        duration: 1500,
                        easing: 'easeInOutQuart'
                      }
                    }}
                  />
                  </div>
                </div>
                <div className="w-1/2 max-h-[180px] overflow-y-auto pl-4">
                  <div className="space-y-1 text-xs">
                    {stats.surveys_by_client.map((item, index) => {
                      const total = stats.surveys_by_client.reduce((sum, c) => sum + c.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex items-center gap-2 justify-end">
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium truncate max-w-[35ch]" style={{ color: '#374151' }}>
                              {item.client_name}
                            </div>
                            <div className="text-gray-500 whitespace-nowrap">
                              ({percentage}%)
                            </div>
                          </div>
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 -ml-1" 
                            style={{ backgroundColor: [
                              'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                            ][index] }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className={`grid grid-cols-1 ${isClientuser ? 'lg:grid-cols-1' : 'lg:grid-cols-2'} gap-6 mb-8`}>
        {/* Top Clients Table - Hidden for clientusers (they only see their own data) */}
        {!isClientuser && (
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-building icon-premium"></i>
                Clients
              </div>
            </h2>
            {stats?.surveys_by_client && stats.surveys_by_client.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.surveys_by_client.map((item, index) => {
                      const total = stats.surveys_by_client.reduce((sum, i) => sum + i.count, 0)
                      const percentage = Math.round((item.count / total) * 100)
                      const colors = [
                        'bg-blue-500',
                        'bg-green-500',
                        'bg-yellow-500',
                        'bg-red-500',
                        'bg-purple-500',
                        'bg-pink-500',
                      ]
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-700">{item.client_name || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-kbl-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>
        )}

        {/* Top Surveyors Table - Hidden for clientusers */}
        {!isClientuser && (
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-person-badge icon-premium"></i>
                Surveyors
              </div>
            </h2>
            {stats?.surveys_by_surveyor && stats.surveys_by_surveyor.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Surveyor</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.surveys_by_surveyor.map((item, index) => {
                      const total = stats.surveys_by_surveyor.reduce((sum, i) => sum + i.count, 0)
                      const percentage = Math.round((item.count / total) * 100)
                      const colors = [
                        'bg-blue-500',
                        'bg-green-500',
                        'bg-yellow-500',
                        'bg-red-500',
                        'bg-purple-500',
                        'bg-pink-500',
                      ]
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-700">{item.surveyor || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-kbl-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clients by Fuel & Savings Analysis - Visible for superusers and clientusers */}
      {(isClientuser || isSuperuser) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Clients by Total Recovered Fuel */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-fuel-pump icon-premium"></i>
                Total Recovered Fuel
              </div>
            </h2>
            {stats?.clients_by_fuel && stats.clients_by_fuel.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Fuel (MT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.clients_by_fuel.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.client_name || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">
                          {parseFloat(item.total_fuel || 0).toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-kbl-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>

          {/* Clients by Total Saving */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-cash-stack icon-premium"></i>
                Total Saving
              </div>
            </h2>
            {stats?.clients_by_saving && stats.clients_by_saving.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Saving ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.clients_by_saving.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.client_name || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">
                          ${parseFloat(item.total_savings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-kbl-gray-500 text-center">No data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Locations Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Countries Table */}
        <div className="card-premium p-5">
          <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="bi bi-globe icon-premium"></i>
              Countries
            </div>
          </h2>
          {stats?.surveys_by_country && stats.surveys_by_country.length > 0 ? (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="table-head-premium sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Country</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.surveys_by_country.map((item, index) => {
                    const total = stats.surveys_by_country.reduce((sum, i) => sum + i.count, 0)
                    const percentage = Math.round((item.count / total) * 100)
                    const colors = [
                      'bg-blue-500',
                      'bg-green-500',
                      'bg-yellow-500',
                      'bg-red-500',
                      'bg-purple-500',
                      'bg-pink-500',
                    ]
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.survey_country || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px]">
              <p className="text-kbl-gray-500 text-center">No data available</p>
            </div>
          )}
        </div>

        {/* Top Ports Table */}
        <div className="card-premium p-5">
          <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="bi bi-water icon-premium"></i>
              Ports
            </div>
          </h2>
          {stats?.surveys_by_port && stats.surveys_by_port.length > 0 ? (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="table-head-premium sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Port</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.surveys_by_port.map((item, index) => {
                    const total = stats.surveys_by_port.reduce((sum, i) => sum + i.count, 0)
                    const percentage = Math.round((item.count / total) * 100)
                    const colors = [
                      'bg-blue-500',
                      'bg-green-500',
                      'bg-yellow-500',
                      'bg-red-500',
                      'bg-purple-500',
                      'bg-pink-500',
                    ]
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.survey_port || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px]">
              <p className="text-kbl-gray-500 text-center">No data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Survey List Section */}
      <div className="card-premium p-5 mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <i className="bi bi-list-ul icon-premium"></i>
            Survey List
          </h2>
          {!surveysLoading && (
            <div className="text-sm text-gray-600">
              {stats && (
                <span>
                  Total: {stats.total_surveys || 0} surveys
                  {surveyTotalCount > 0 && surveyTotalPages > 1 && (
                    <span className="ml-2">
                      (Showing page {surveyPage} of {surveyTotalPages})
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        {surveysLoading ? (
          <div className="flex items-center justify-center h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#ea580c]"></div>
            <p className="text-gray-600 ml-4 text-sm">Loading surveys...</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="flex items-center justify-center h-[200px]">
            <p className="text-gray-500 text-sm">No surveys found</p>
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="table-head-premium rounded-lg px-4 py-2.5 mb-3 shadow-[0_1px_6px_rgba(234,88,12,0.3)]">
              <div className="grid grid-cols-7 gap-3 text-xs font-semibold uppercase tracking-wide">
                <div className="col-span-1">
                  <span>Client</span>
                </div>
                <div className="col-span-1">
                  <span>Survey Type</span>
                </div>
                <div className="col-span-1">
                  <span>Vessel</span>
                </div>
                <div className="col-span-1">
                  <span>Country</span>
                </div>
                <div className="col-span-1">
                  <span>Port</span>
                </div>
                <div className="col-span-1">
                  <span>Reference Number</span>
                </div>
                <div className="col-span-1 text-right">
                  <span>Report</span>
                </div>
              </div>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {surveys.map((survey) => {
                const country = survey.survey_country || '-'
                const referenceNumber = survey.reference_number || '-'

                return (
                  <div
                    key={survey.reference_number || survey.id}
                    className="bg-white transition-all rounded-lg shadow-sm hover:shadow-md border border-gray-200"
                  >
                    <div className="pl-4 pr-6 py-3">
                      <div className="grid grid-cols-7 gap-3 items-center text-sm">
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-orange-500 text-base flex-shrink-0">👤</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.client_name || '-'}>
                              {survey.client_name || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-orange-500 text-base flex-shrink-0">📋</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.survey_type || '-'}>
                              {survey.survey_type || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-orange-500 text-base flex-shrink-0">🚢</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.vessel_name || '-'}>
                              {survey.vessel_name || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-orange-500 text-base flex-shrink-0">🌍</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={country}>
                              {country}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-orange-500 text-base flex-shrink-0">⚓</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.survey_port || '-'}>
                              {survey.survey_port || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <img 
                              src={`${process.env.PUBLIC_URL || ''}/images/K_logo.jpeg`}
                              alt="KBL Logo" 
                              className="w-5 h-5 object-contain flex-shrink-0"
                            />
                            <span className="text-gray-500 truncate whitespace-nowrap" title={referenceNumber}>
                              {referenceNumber}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => survey.has_reports && setReportsModalRef(survey.reference_number)}
                            disabled={!survey.has_reports}
                            className={`w-8 h-8 flex items-center justify-center rounded-md transition-all duration-200 ${
                              survey.has_reports
                                ? 'bg-premium text-white shadow-[0_2px_8px_rgba(234,88,12,0.3)] hover:shadow-[0_4px_14px_rgba(234,88,12,0.4)]'
                                : 'bg-gray-100 text-gray-300 border border-gray-200 cursor-not-allowed'
                            }`}
                            title={survey.has_reports ? "View / download this survey's reports" : 'No reports uploaded yet'}
                          >
                            <i className="bi bi-folder2-open"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Pagination */}
            {surveyTotalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, surveyPage - 1)
                    setSurveyPage(newPage)
                    fetchSurveys(newPage)
                  }}
                  disabled={surveyPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="bi bi-chevron-left mr-1"></i>
                  Previous
                </button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">
                  Page {surveyPage} of {surveyTotalPages}
                </span>
                <button
                  onClick={() => {
                    const newPage = Math.min(surveyTotalPages, surveyPage + 1)
                    setSurveyPage(newPage)
                    fetchSurveys(newPage)
                  }}
                  disabled={surveyPage === surveyTotalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <i className="bi bi-chevron-right ml-1"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Report download modal - view/download only from the Dashboard; uploading stays on the main Survey List */}
      {reportsModalRef && (
        <SurveyReportsModal
          referenceNumber={reportsModalRef}
          canUpload={false}
          onClose={() => setReportsModalRef(null)}
        />
      )}

      {/* SoA Modal */}
      {showSoaModal && soaData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-premium text-white p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">Statement of Account</h2>
                  <p className="text-white/80 mt-1">
                    {soaData.filters?.start_date && soaData.filters?.end_date 
                      ? `Period: ${soaData.filters.start_date} to ${soaData.filters.end_date}`
                      : 'All Time'
                    }
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={exportSOA}
                    className="px-4 py-2 text-sm font-semibold bg-white text-[#c2410c] rounded-lg hover:bg-[#fff7ed] transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Export Excel
                  </button>
                  <button
                    onClick={() => setShowSoaModal(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {/* SoA Data Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="table-head-premium">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ref No</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Survey Type</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vessel</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Country, Port</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">LSFO (Diff)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">LSFO (Recovery)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">HSFO (Diff)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">HSFO (Recovery)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">MGO (Diff)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">MGO (Recovery)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Incentive</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Survey Fee</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expenses</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Others</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {soaData.data?.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item.reference_no}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.client}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.survey_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.attendance_date}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.vessel}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{item.country_port}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.lsfo_diff?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.lsfo_recovery?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.hsfo_diff?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.hsfo_recovery?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.mgo_diff?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.mgo_recovery?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.incentive?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.survey_fee?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.expenses?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.others?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">${item.discount?.toFixed(2) || '0.00'}</td>
                        <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">${item.total?.toFixed(2) || '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="6" className="px-4 py-3 text-sm font-medium text-gray-900">TOTAL</td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.lsfo_diff?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.lsfo_recovery?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.hsfo_diff?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.hsfo_recovery?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.mgo_diff?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.mgo_recovery?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.incentive?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.survey_fee?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.expenses?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.others?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right text-gray-900">
                        ${soaData.totals?.discount?.toFixed(2) || '0.00'}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-right text-gray-900">
                        ${soaData.grand_total?.toFixed(2) || '0.00'}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  )
}

export default Dashboard

